import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { getWallets, getWalletStatement, addWallet, updateWallet, deleteWallet, transferBetweenWallets } from '../utils/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Wallet, Plus, Edit, Trash2, ArrowLeftRight, ChevronRight, FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatCurrency } from '../utils/formatCurrency';
import { useNumberFormat } from '../hooks/useNumberFormat';

const walletBrandColors = {
  mpesa: '#4CAF50',
  crdb: '#00A859',
  airtel: '#FF0000',
  custom: '#064E3B'
};

const formatStatementDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const csvValue = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

export const Wallets = () => {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isStatementOpen, setIsStatementOpen] = useState(false);
  const [currentWallet, setCurrentWallet] = useState(null);
  const [saving, setSaving] = useState({ add: false, edit: false, delete: false, transfer: false });
  const [statementLoading, setStatementLoading] = useState(false);
  const [statement, setStatement] = useState(null);
  const [statementDates, setStatementDates] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'mpesa',
    transaction_charge_rate: '0'
  });
  
  // Number format hooks for live comma formatting
  const balanceAmount = useNumberFormat('0');
  const transferAmount = useNumberFormat('');
  
  const [transferData, setTransferData] = useState({
    from_wallet_id: '',
    to_wallet_id: '',
    date: new Date().toISOString().split('T')[0]  // Default to today
  });
  
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, wallet: null, reason: '' });

  const viewWalletTransactions = (walletId) => {
    navigate(`/transactions?wallet_id=${walletId}`);
  };

  const loadStatement = async (wallet = currentWallet) => {
    if (!wallet) return;
    setStatementLoading(true);
    try {
      const data = await getWalletStatement(wallet.id, statementDates.start_date, statementDates.end_date);
      setStatement(data);
    } catch (error) {
      toast.error(error.message || 'Failed to load wallet statement');
    } finally {
      setStatementLoading(false);
    }
  };

  const openStatement = async (wallet) => {
    setCurrentWallet(wallet);
    setStatement(null);
    setIsStatementOpen(true);
    await loadStatement(wallet);
  };

  const downloadStatement = () => {
    if (!statement) return;

    const rows = [
      ['Wallet', statement.wallet.name],
      ['Start Date', statement.start_date || 'All'],
      ['End Date', statement.end_date || 'All'],
      ['Opening Balance', statement.opening_balance],
      ['Closing Balance', statement.closing_balance],
      [],
      ['Date', 'Type', 'Category / Source', 'Narration', 'Amount', 'Charge', 'Running Balance'],
      ...statement.transactions.map((row) => [
        formatStatementDate(row.date),
        row.type.replace('_', ' '),
        row.category || '',
        row.narration || '',
        row.amount,
        row.transaction_charge || 0,
        row.running_balance
      ])
    ];

    const csv = rows.map((row) => row.map(csvValue).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const safeWalletName = (statement.wallet.name || 'wallet').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
    link.href = URL.createObjectURL(blob);
    link.download = `${safeWalletName || 'wallet'}-statement-${statement.start_date || 'all'}-to-${statement.end_date || 'all'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  useEffect(() => {
    fetchWallets();
  }, []);

  const fetchWallets = async () => {
    try {
      const data = await getWallets();
      setWallets(data);
    } catch (error) {
      toast.error('Failed to load wallets');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (saving.add) return;
    setSaving((state) => ({ ...state, add: true }));
    try {
      await addWallet({
        name: formData.name,
        type: formData.type,
        balance: parseFloat(balanceAmount.numericValue || '0'),
        transaction_charge_rate: parseFloat(formData.transaction_charge_rate)
      });
      toast.success('Wallet created successfully');
      setIsAddOpen(false);
      setFormData({ name: '', type: 'mpesa', transaction_charge_rate: '0' });
      balanceAmount.setValue('0');
      fetchWallets();
    } catch (error) {
      toast.error(error.message || 'Failed to create wallet');
    } finally {
      setSaving((state) => ({ ...state, add: false }));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (saving.edit) return;
    setSaving((state) => ({ ...state, edit: true }));
    try {
      await updateWallet(currentWallet.id, {
        name: formData.name,
        transaction_charge_rate: parseFloat(formData.transaction_charge_rate)
      });
      toast.success('Wallet updated successfully');
      setIsEditOpen(false);
      setCurrentWallet(null);
      fetchWallets();
    } catch (error) {
      toast.error(error.message || 'Failed to update wallet');
    } finally {
      setSaving((state) => ({ ...state, edit: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.reason?.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }
    if (saving.delete) return;
    setSaving((state) => ({ ...state, delete: true }));
    try {
      await deleteWallet(deleteConfirm.wallet.id, deleteConfirm.reason);
      toast.success('Wallet deleted successfully');
      setDeleteConfirm({ show: false, wallet: null, reason: '' });
      fetchWallets();
    } catch (error) {
      toast.error(error.message || 'Failed to delete wallet');
    } finally {
      setSaving((state) => ({ ...state, delete: false }));
    }
  };

  const openDeleteConfirm = (wallet) => {
    setDeleteConfirm({ show: true, wallet, reason: '' });
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (saving.transfer) return;
    setSaving((state) => ({ ...state, transfer: true }));
    try {
      await transferBetweenWallets(
        transferData.from_wallet_id,
        transferData.to_wallet_id,
        parseFloat(transferAmount.numericValue),
        transferData.date ? new Date(transferData.date).toISOString() : null
      );
      toast.success('Transfer successful');
      setIsTransferOpen(false);
      setTransferData({ from_wallet_id: '', to_wallet_id: '', date: new Date().toISOString().split('T')[0] });
      transferAmount.reset();
      fetchWallets();
    } catch (error) {
      toast.error(error.message || 'Transfer failed');
    } finally {
      setSaving((state) => ({ ...state, transfer: false }));
    }
  };

  const openEdit = (wallet) => {
    setCurrentWallet(wallet);
    setFormData({
      name: wallet.name,
      type: wallet.type,
      transaction_charge_rate: wallet.transaction_charge_rate.toString()
    });
    setIsEditOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-stone-600">Loading wallets...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-8 pb-20 lg:pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="text-4xl font-bold text-stone-900 tracking-tight mb-2">Wallets</h1>
            <p className="text-stone-600">Manage your payment wallets</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={isTransferOpen} onOpenChange={setIsTransferOpen}>
              <DialogTrigger asChild>
                <Button className="bg-stone-100 text-stone-900 hover:bg-stone-200 rounded-full" data-testid="transfer-button">
                  <ArrowLeftRight className="w-4 h-4 mr-2" />
                  Transfer
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="transfer-dialog">
                <DialogHeader>
                  <DialogTitle>Transfer Between Wallets</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleTransfer} className="space-y-4">
                  <div>
                    <Label>From Wallet</Label>
                    <Select
                      value={transferData.from_wallet_id}
                      onValueChange={(value) => setTransferData({ ...transferData, from_wallet_id: value })}
                    >
                      <SelectTrigger className="bg-stone-50" data-testid="transfer-from-wallet-select">
                        <SelectValue placeholder="Select wallet" />
                      </SelectTrigger>
                      <SelectContent>
                        {wallets.map(w => (
                          <SelectItem key={w.id} value={w.id}>{w.name} ({formatCurrency(w.balance)})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>To Wallet</Label>
                    <Select
                      value={transferData.to_wallet_id}
                      onValueChange={(value) => setTransferData({ ...transferData, to_wallet_id: value })}
                    >
                      <SelectTrigger className="bg-stone-50" data-testid="transfer-to-wallet-select">
                        <SelectValue placeholder="Select wallet" />
                      </SelectTrigger>
                      <SelectContent>
                        {wallets.map(w => (
                          <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount (TZS)</Label>
                    <Input
                      type="tel"
                      data-testid="transfer-amount-input"
                      value={transferAmount.displayValue}
                      onChange={transferAmount.handleChange}
                      placeholder="0"
                      className="bg-stone-50 text-xl h-12 font-semibold text-center"
                      required
                    />
                    <p className="text-xs text-stone-500 mt-1 text-center">
                      {transferAmount.numericValue ? `TZS ${transferAmount.displayValue}` : 'Enter amount'}
                    </p>
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      data-testid="transfer-date-input"
                      value={transferData.date}
                      onChange={(e) => setTransferData({ ...transferData, date: e.target.value })}
                      className="bg-stone-50"
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <Button type="submit" disabled={saving.transfer} className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="transfer-submit-button">
                    {saving.transfer ? 'Saving...' : 'Transfer'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="add-wallet-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Wallet
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-wallet-dialog">
                <DialogHeader>
                  <DialogTitle>Add New Wallet</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAdd} className="space-y-4">
                  <div>
                    <Label>Wallet Name</Label>
                    <Input
                      data-testid="wallet-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="My MPESA"
                      className="bg-stone-50"
                      required
                    />
                  </div>
                  <div>
                    <Label>Wallet Type</Label>
                    <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                      <SelectTrigger className="bg-stone-50" data-testid="wallet-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mpesa">MPESA</SelectItem>
                        <SelectItem value="crdb">CRDB</SelectItem>
                        <SelectItem value="airtel">AIRTEL</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Initial Balance (TZS)</Label>
                    <Input
                      type="tel"
                      data-testid="wallet-initial-balance-input"
                      value={balanceAmount.displayValue}
                      onChange={balanceAmount.handleChange}
                      placeholder="0"
                      className="bg-stone-50 text-xl h-12 font-semibold text-center"
                    />
                    <p className="text-xs text-stone-500 mt-1 text-center">
                      {balanceAmount.numericValue ? `TZS ${balanceAmount.displayValue}` : 'Enter amount'}
                    </p>
                  </div>
                  <Button type="submit" disabled={saving.add} className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="wallet-submit-button">
                    {saving.add ? 'Saving...' : 'Create Wallet'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </motion.div>

        {wallets.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-stone-200 rounded-xl p-12 text-center"
          >
            <Wallet className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-900 mb-2">No wallets yet</h3>
            <p className="text-stone-600 mb-6">Create your first wallet to start tracking your cashflow</p>
            <Button onClick={() => setIsAddOpen(true)} className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="create-first-wallet-cta">
              <Plus className="w-4 h-4 mr-2" />
              Create Wallet
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wallets.map((wallet, index) => (
              <motion.div
                key={wallet.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border-2 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer"
                style={{ borderTopColor: walletBrandColors[wallet.type] }}
                data-testid={`wallet-card-${wallet.id}`}
                onClick={() => viewWalletTransactions(wallet.id)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-stone-600 uppercase tracking-wide">{wallet.type}</p>
                    <p className="text-xl font-bold text-stone-900 mt-1">{wallet.name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Wallet className="w-6 h-6" style={{ color: walletBrandColors[wallet.type] }} strokeWidth={1.5} />
                    <ChevronRight className="w-5 h-5 text-stone-400" />
                  </div>
                </div>
                <div className="mb-6 pt-4 border-t border-stone-100">
                  <p className="text-sm text-stone-600 mb-1">Current Balance</p>
                  <p className="text-3xl font-bold text-stone-900">{formatCurrency(wallet.balance)}</p>
                  <p className="text-xs text-stone-500 mt-2">Transaction Charge: {wallet.transaction_charge_rate}%</p>
                </div>
                <div className="grid grid-cols-3 gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    onClick={() => openStatement(wallet)}
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    data-testid={`statement-wallet-${wallet.id}`}
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Statement
                  </Button>
                  <Button
                    onClick={() => openEdit(wallet)}
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    data-testid={`edit-wallet-${wallet.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => openDeleteConfirm(wallet)}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-orange-800 hover:bg-orange-50"
                    data-testid={`delete-wallet-${wallet.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Statement Dialog */}
      <Dialog open={isStatementOpen} onOpenChange={setIsStatementOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="wallet-statement-dialog">
          <DialogHeader>
            <DialogTitle>{currentWallet ? `${currentWallet.name} Statement` : 'Wallet Statement'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-hidden flex flex-col">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={statementDates.start_date}
                  onChange={(e) => setStatementDates({ ...statementDates, start_date: e.target.value })}
                  className="bg-stone-50"
                  data-testid="statement-start-date"
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={statementDates.end_date}
                  onChange={(e) => setStatementDates({ ...statementDates, end_date: e.target.value })}
                  className="bg-stone-50"
                  data-testid="statement-end-date"
                />
              </div>
              <Button
                type="button"
                onClick={() => loadStatement()}
                disabled={statementLoading}
                className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full"
                data-testid="statement-apply-button"
              >
                {statementLoading ? 'Loading...' : 'Apply'}
              </Button>
              <Button
                type="button"
                onClick={downloadStatement}
                disabled={!statement || statementLoading}
                variant="outline"
                className="rounded-full"
                data-testid="statement-download-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </div>

            {statement && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-stone-50 rounded-lg p-3">
                  <p className="text-xs uppercase tracking-wide text-stone-500">Opening Balance</p>
                  <p className="text-lg font-semibold text-stone-900">{formatCurrency(statement.opening_balance)}</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-3">
                  <p className="text-xs uppercase tracking-wide text-stone-500">Closing Balance</p>
                  <p className="text-lg font-semibold text-stone-900">{formatCurrency(statement.closing_balance)}</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-3">
                  <p className="text-xs uppercase tracking-wide text-stone-500">Transactions</p>
                  <p className="text-lg font-semibold text-stone-900">{statement.transactions.length}</p>
                </div>
              </div>
            )}

            <div className="border rounded-lg overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-600 sticky top-0">
                  <tr>
                    <th className="text-left font-medium p-3 whitespace-nowrap">Date</th>
                    <th className="text-left font-medium p-3 whitespace-nowrap">Type</th>
                    <th className="text-left font-medium p-3 whitespace-nowrap">Category / Source</th>
                    <th className="text-left font-medium p-3 min-w-[220px]">Narration</th>
                    <th className="text-right font-medium p-3 whitespace-nowrap">Amount</th>
                    <th className="text-right font-medium p-3 whitespace-nowrap">Charge</th>
                    <th className="text-right font-medium p-3 whitespace-nowrap">Running Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {statementLoading ? (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-stone-500">Loading statement...</td>
                    </tr>
                  ) : statement?.transactions?.length ? (
                    statement.transactions.map((row) => (
                      <tr key={row.id} className="border-t border-stone-100">
                        <td className="p-3 whitespace-nowrap text-stone-700">{formatStatementDate(row.date)}</td>
                        <td className="p-3 whitespace-nowrap">
                          <span className="px-2 py-1 rounded-full bg-stone-100 text-stone-700 text-xs font-medium">
                            {row.type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="p-3 text-stone-800">{row.category || '-'}</td>
                        <td className="p-3 text-stone-600">{row.narration || '-'}</td>
                        <td className={`p-3 text-right whitespace-nowrap font-medium ${row.amount < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                          {formatCurrency(row.amount)}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap text-stone-600">{formatCurrency(row.transaction_charge || 0)}</td>
                        <td className="p-3 text-right whitespace-nowrap font-semibold text-stone-900">{formatCurrency(row.running_balance)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-stone-500">No transactions found for this date range.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent data-testid="edit-wallet-dialog">
          <DialogHeader>
            <DialogTitle>Edit Wallet</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <Label>Wallet Name</Label>
              <Input
                data-testid="edit-wallet-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-stone-50"
                required
              />
            </div>
            <div>
              <Label>Transaction Charge Rate (%)</Label>
              <Input
                type="number"
                step="0.01"
                data-testid="edit-wallet-charge-rate-input"
                value={formData.transaction_charge_rate}
                onChange={(e) => setFormData({ ...formData, transaction_charge_rate: e.target.value })}
                className="bg-stone-50"
              />
            </div>
            <Button type="submit" disabled={saving.edit} className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full" data-testid="edit-wallet-submit-button">
              {saving.edit ? 'Saving...' : 'Update Wallet'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, wallet: null, reason: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Wallet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-stone-600">
              Are you sure you want to delete this wallet? This action cannot be undone.
            </p>
            {deleteConfirm.wallet && (
              <div className="bg-stone-50 rounded-lg p-3 text-sm">
                <p className="font-medium">{deleteConfirm.wallet.name}</p>
                <p className="text-stone-500">
                  Balance: {formatCurrency(deleteConfirm.wallet.balance)}
                </p>
              </div>
            )}
            <div>
              <Label className="text-stone-700">Reason for Deletion *</Label>
              <Input
                data-testid="delete-wallet-reason-input"
                value={deleteConfirm.reason}
                onChange={(e) => setDeleteConfirm({ ...deleteConfirm, reason: e.target.value })}
                placeholder="Why are you deleting this wallet?"
                className="mt-1"
                required
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm({ show: false, wallet: null, reason: '' })}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={saving.delete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-wallet-btn"
              >
                {saving.delete ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
